import type {
  PilotOnboardingAuditEvent,
  PilotOnboardingProgramConfiguration,
  PilotOnboardingRelationshipSelection,
  PilotOnboardingRequestSnapshot,
  PilotOnboardingSource,
  PilotOnboardingStatus,
  PilotOnboardingSteps,
} from '@shared';

import { WaterlilyClient } from '@/api/waterlily';

export interface PilotAdvisorIdentity {
  advisorId: string;
  advisorEmail: string;
  advisorFirstName: string | null;
  advisorLastName: string | null;
  organizationName: string | null;
  organizationDisplayName: string | null;
}

export type PilotRelationshipIdentity = Omit<PilotAdvisorIdentity, 'advisorId'> & {
  advisorId: string | null;
};

export interface PilotProgram extends Record<string, unknown> {
  id: string;
  programKey: string;
  displayName: string;
  shortName: string;
  configuration: PilotOnboardingProgramConfiguration;
  configVersion: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  referencedAdvisors: Array<PilotAdvisorIdentity & { ownsActiveSubscription: boolean }>;
}

export interface PilotOnboardingRecord extends Record<string, unknown> {
  id: string;
  pilotProgramId: string;
  source: PilotOnboardingSource;
  email: string;
  advisorId: string | null;
  roleKey: string | null;
  subscriptionOwnerAdvisorId: string | null;
  subscriptionSeatId: string | null;
  seatExpiresDateTime: string | null;
  status: PilotOnboardingStatus;
  requestSnapshot?: PilotOnboardingRequestSnapshot | null;
  steps?: PilotOnboardingSteps;
  auditLog?: PilotOnboardingAuditEvent[];
  createdAt: string;
  updatedAt?: string;
  program?: Omit<PilotProgram, 'referencedAdvisors'> | null;
  advisor?: PilotAdvisorIdentity | null;
  subscriptionOwner?: PilotAdvisorIdentity | null;
}

export type PilotOnboardingInput = {
  pilotProgramId: string;
  email: string;
  roleKey: string;
  seatExpiresDateTime?: string | null;
  relationships?: PilotOnboardingRelationshipSelection[];
};

export type PilotProgramUpdateInput = {
  displayName: string;
  shortName: string;
  configuration: PilotOnboardingProgramConfiguration;
  expectedConfigVersion: number;
};

export type PilotOnboardingPreflight = {
  email: string;
  eligible: boolean;
  advisorExists: boolean;
  advisor: PilotAdvisorIdentity | null;
  blockers: Array<
    | 'pending_invitation'
    | 'active_seat'
    | 'owned_subscription'
    | 'open_onboarding'
    | 'legacy_pilot_user'
  >;
  pendingInvitations: Array<{
    subscriptionSeatId: string;
    status: string;
    expiresDateTime: string | null;
  }>;
  activeSeat: { subscriptionSeatId: string; status: string } | null;
  ownedSubscription: { stripeSubscriptionId: string; subscriptionStatus: string } | null;
  openOnboarding: PilotOnboardingRecord | null;
  legacyPilotUser: boolean;
  preRegisteredRelationships: Array<{
    relatedAdvisorId: string | null;
    relatedAdvisorEmail: string;
    relatedAdvisor: PilotAdvisorIdentity | null;
    accessType: PilotOnboardingRelationshipSelection['accessType'];
    preRegisteredRelationshipIds: string[];
  }>;
};

export type PilotAdvisorSearchResult = PilotAdvisorIdentity & {
  ownsActiveSubscription: boolean;
};

export const pilotOnboardingKeys = {
  all: ['pilotOnboarding'] as const,
  programs: () => [...pilotOnboardingKeys.all, 'programs'] as const,
  onboardings: () => [...pilotOnboardingKeys.all, 'onboardings'] as const,
  preflight: (programId: string, email: string) =>
    [...pilotOnboardingKeys.all, 'preflight', programId, email] as const,
  advisorSearch: (purpose: string, query: string) =>
    [...pilotOnboardingKeys.all, 'advisorSearch', purpose, query] as const,
};

export const pilotOnboardingApi = {
  fetchPrograms: () => WaterlilyClient.get<PilotProgram[]>('/wizard-pilot-programs'),
  updateProgram: (pilotProgramId: string, body: PilotProgramUpdateInput) =>
    WaterlilyClient.put<PilotProgram>(`/wizard-pilot-program/${pilotProgramId}`, body),
  fetchOnboardings: () => WaterlilyClient.get<PilotOnboardingRecord[]>('/wizard-pilot-onboardings'),
  preflight: (email: string, pilotProgramId: string) =>
    WaterlilyClient.post<PilotOnboardingPreflight>('/wizard-pilot-onboarding-preflight', {
      email,
      pilotProgramId,
    }),
  searchAdvisors: (query: string, purpose: 'relationship' | 'subscription_owner') =>
    WaterlilyClient.post<PilotAdvisorSearchResult[]>('/wizard-pilot-advisor-search', {
      query,
      purpose,
    }),
  create: (body: PilotOnboardingInput) =>
    WaterlilyClient.post<PilotOnboardingRecord>('/wizard-pilot-onboarding', body),
  update: (pilotOnboardingId: string, body: PilotOnboardingInput) =>
    WaterlilyClient.put<PilotOnboardingRecord>(
      `/wizard-pilot-onboarding/${pilotOnboardingId}`,
      body,
    ),
  retry: (pilotOnboardingId: string) =>
    WaterlilyClient.post<PilotOnboardingRecord>(
      `/wizard-pilot-onboarding-retry/${pilotOnboardingId}`,
      {},
    ),
  resend: (pilotOnboardingId: string) =>
    WaterlilyClient.post<PilotOnboardingRecord>(
      `/wizard-pilot-onboarding-resend/${pilotOnboardingId}`,
      {},
    ),
  delete: (pilotOnboardingId: string) =>
    WaterlilyClient.delete<{
      id: string;
      status: 'deleted';
      retainedAppliedAccess: boolean;
      deletedAt: string;
    }>(`/wizard-pilot-onboarding/${pilotOnboardingId}`),
  deletePreRegisteredRelationships: (body: { email: string; relationshipIds: string[] }) =>
    WaterlilyClient.post<{ deleted: number }>(
      '/wizard-pilot-pre-registered-relationship-deletion',
      body,
    ),
};
